// Design A ("Corporate Band") Sales Order -- used ONLY for Print Preview
// (via SalesOrderPrint.jsx) when this company's institution_profile.
// print_template is 'design_a'. Mirrors
// backend/src/pdfTemplates/designA/salesOrderAdapter.js's config mapping
// exactly. SalesOrderVoucherBody.jsx (legacy, still used for the live
// entry-screen preview and for the 'legacy' print_template) is untouched.
//
// Carried over from the legacy template (see salesOrderAdapter.js for the
// full rationale): approval_status === "pending" gate rendered inline (the
// real enforcement point for both Download and Print); tax is a single
// combined row, never CGST/SGST/IGST; no Paid/Due rows or bank details.
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Typography } from "antd";

import { numberFormat, formatToCompanyTimezone } from "../../../../../lib/functions";
import { convertAmountToWordsDesignA } from "../../../../../lib/designAWords";
import { fitItemName } from "../../../../../lib/designAItemFit";
import { formatPercent } from "../../../../../lib/designANumberFormat";
import { getSalesOrderRecordWithDetailsApi } from "../../../../../apiservice/sales";
import { getInstitutionApi } from "../../../../../apiservice/setting";
import { getPrimaryBankAccountApi, getBankAccountByIdsApi } from "../../../../../apiservice/accounts";
import { bankAccountToConfig } from "../../../../../lib/designABankAccount";
import { selectAuthInfo } from "../../../../Auth/authSelectors";
import VoucherBodyDesignA from "../../../../../components/common/VoucherBodyDesignA";

const { Title, Text } = Typography;

const isBonusItem = (item) => Number(item.item_rate) === 0;

// Row budgets recalibrated for the taller "rich item" row (name line + an
// optional batch/description sub-line, see da-table--rich-item in
// VoucherBodyDesignA.css) -- ~42px/row vs. the default 28px, so ~2/3 as
// many rows fit per page. Mirrors salesOrderAdapter.js exactly.
const SALES_ORDER_ROW_BUDGETS = { single: 10, first: 16, middle: 20, last: 9 };

// Same fixed-layout column widths as salesOrderAdapter.js -- see that
// file's comment for the reasoning. Keep both in sync.
const PAGE_CONTENT_WIDTH = 698;
const COL_WIDTH = { hash: 28, rate: 100, per: 50, qty: 44, disc: 64, tax: 54, amount: 104, quantity: 70, soldQuantity: 70 };
const CELL_PADDING = 16;
const CHAR_WIDTH_PX = 5.3;

function ItemCell({ name, maxChars, bonus, batchName, description }) {
  const fit = fitItemName(name, { maxChars });
  const subParts = [];
  if (batchName) subParts.push(`Batch: ${batchName}`);
  if (description) subParts.push(description);
  return (
    <div className="da-item-cell">
      <span className="da-item-name" style={{ fontSize: `${fit.fontSizePercent}%` }}>
        {fit.text}
        {bonus && <span className="da-col-batch"> BONUS</span>}
      </span>
      {subParts.length > 0 && <div className="da-item-sub">{subParts.join(" · ")}</div>}
    </div>
  );
}

export default function SalesOrderVoucherBodyDesignA({ sale_o_id, showStamp = false, showSignature = false }) {
  const [data, setData] = useState(null);
  const [institution, setInstitution] = useState(null);
  const [primaryBankAccount, setPrimaryBankAccount] = useState(null);
  const [paymentBankAccount, setPaymentBankAccount] = useState(null);
  const authInfo = useSelector(selectAuthInfo);

  useEffect(() => {
    if (!sale_o_id) return;
    getSalesOrderRecordWithDetailsApi({ sale_o_id, from: "voucher" }).then((res) => {
      if (res?.data?.length) setData(res.data[0]);
    });
  }, [sale_o_id]);

  // This order's own advance payment (if any) can be received into a bank
  // account that isn't marked primary -- when that's the case, show the
  // account the money actually went into (see getBankAccountByIds's
  // comment in backend/src/controllers/accounts.js). Safe no-op if this
  // record has no `.trans` field.
  useEffect(() => {
    if (!data) return;
    const toAccIds = (data.trans || []).map((t) => t.to_acc_id).filter(Boolean);
    if (!toAccIds.length) return;
    getBankAccountByIdsApi(toAccIds).then((res) => setPaymentBankAccount(res?.data?.data || null)).catch(() => {});
  }, [data]);

  useEffect(() => {
    // Legacy SalesOrderPrint.jsx never fetches institution (so stamp/logo is
    // blank there today) -- fetched here instead so Print Preview matches
    // the backend PDF/WhatsApp path, which does have real institution data
    // via GENERIC_INSTITUTION_QUERY.
    getInstitutionApi().then((res) => setInstitution(res?.data || null)).catch(() => {});
    getPrimaryBankAccountApi().then((res) => setPrimaryBankAccount(res?.data?.data || null)).catch(() => {});
  }, []);

  if (!data) return null;

  // Real enforcement point for both Download and Print, same as the legacy
  // component -- do not drop this.
  if (data.approval_status === "pending") {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Title level={4}>Pending Approval</Title>
        <Text>This sales order is pending approval and cannot be downloaded or printed yet.</Text>
      </div>
    );
  }

  const record = data;
  const currency = authInfo?.currency;
  const country = authInfo?.country || "";
  const isIndia = country.toLowerCase() === "india";
  const taxLabel = isIndia ? "GST" : "VAT";
  const isGSTDisabled = isIndia && authInfo?.gst_enabled == "no";
  const isUAE = country.trim().toLowerCase() === "united arab emirates";
  const subscriptionType = (authInfo?.subscription_type || "").toLowerCase();
  const isPharma = subscriptionType.includes("pharma") || subscriptionType.includes("pharmabooks");
  const accMgrLabel = isUAE && isPharma ? "Rep." : "Account Manager";
  const taxRegLabel = isUAE ? "TRN No" : "Tax Reg No";

  const isIndividual = record.is_cal_type === "individual";
  const items = record.details || [];
  const hasBonusItems = items.some(isBonusItem);

  // No dedicated "Batch" column -- matches the legacy design, which never
  // had one either: batch_name (when present) and item_description (when
  // present) both go as sub-lines under the item name instead.
  const usedWidth =
    COL_WIDTH.hash + COL_WIDTH.rate + COL_WIDTH.per +
    (isIndividual
      ? COL_WIDTH.qty + COL_WIDTH.disc + (!isGSTDisabled ? COL_WIDTH.tax : 0) + COL_WIDTH.amount
      : COL_WIDTH.quantity + COL_WIDTH.soldQuantity + COL_WIDTH.amount);
  const itemMaxChars = Math.max(20, Math.floor((PAGE_CONTENT_WIDTH - usedWidth - CELL_PADDING) / CHAR_WIDTH_PX));

  const columns = [
    { label: "#", width: `${COL_WIDTH.hash}px` },
    { label: "Item & Description", cellClassName: "da-item-td" },
    { label: "Rate", align: "right", width: `${COL_WIDTH.rate}px` },
    { label: "Per", align: "right", width: `${COL_WIDTH.per}px` },
  ];
  if (isIndividual) {
    columns.push({ label: "Qty", align: "right", width: `${COL_WIDTH.qty}px` });
    columns.push({ label: "Disc %", align: "right", width: `${COL_WIDTH.disc}px` });
    if (!isGSTDisabled) columns.push({ label: `${taxLabel} %`, align: "right", width: `${COL_WIDTH.tax}px` });
    columns.push({ label: "Total", align: "right", width: `${COL_WIDTH.amount}px` });
  } else {
    columns.push({ label: "Quantity", align: "right", width: `${COL_WIDTH.quantity}px` });
    columns.push({ label: "Sold Quantity", align: "right", width: `${COL_WIDTH.soldQuantity}px` });
    columns.push({ label: "Amount", align: "right", width: `${COL_WIDTH.amount}px` });
  }

  const rows = items.map((d, i) => {
    const bonus = isBonusItem(d);
    const cells = [
      String(i + 1),
      <ItemCell key="n" name={d.item_name} maxChars={itemMaxChars} bonus={bonus} batchName={d.batch_name} description={d.item_description} />,
      <span className="da-num-cell" key="r">{numberFormat(d.item_rate)}</span>,
      <span className="da-num-cell" key="p">{d.unit_symbol || d.per_unit_symbol}</span>,
    ];

    let rawAmount;
    if (isIndividual) {
      cells.push(<span className="da-num-cell" key="q">{formatPercent(d.item_qty)}</span>);
      cells.push(<span className="da-num-cell" key="d">{formatPercent(d.item_discount_per)}</span>);
      if (!isGSTDisabled) cells.push(<span className="da-num-cell" key="t">{formatPercent(d.item_tax_per)}</span>);
      let total = Number(d.item_qty || 0) * Number(d.item_rate || 0);
      total -= (Number(d.item_discount_per || 0) / 100) * total;
      total += (Number(d.item_tax_per || 0) / 100) * total;
      rawAmount = total;
      cells.push(<span className="da-amount-cell" key="a">{numberFormat(rawAmount)}</span>);
    } else {
      const doneQty = Number(d.done_item_qty || 0);
      cells.push(<span className="da-num-cell" key="q">{formatPercent(d.item_qty)} {d.unit_symbol}</span>);
      cells.push(<span className="da-num-cell" key="sq">{doneQty} {d.unit_symbol}</span>);
      rawAmount = Number(d.item_total || 0);
      cells.push(<span className="da-amount-cell" key="a">{numberFormat(rawAmount)}</span>);
    }

    return { cells, isBonus: bonus, rawAmount };
  });

  const totals = items.reduce(
    (acc, d) => {
      const qty = Number(d.item_qty || 0);
      const rate = Number(d.item_rate || 0);
      const base = qty * rate;
      const discountAmt = (Number(d.item_discount_per || 0) / 100) * base;
      const afterDiscount = base - discountAmt;
      const taxAmt = (Number(d.item_tax_per || 0) / 100) * afterDiscount;
      acc.subTotal += base;
      acc.discountTotal += discountAmt;
      acc.grandTotal += afterDiscount + taxAmt;
      return acc;
    },
    { subTotal: 0, discountTotal: 0, grandTotal: 0 }
  );

  const serviceCost = (record.otherServiceTrans || []).reduce((sum, t) => sum + Number(t.tran_amount || 0), 0);
  const grandTotalInWords = (isIndividual ? totals.grandTotal : record.total_amount || 0) + serviceCost;

  const totalsRows = [];
  totalsRows.push({ label: "Sub Total", value: numberFormat(isIndividual ? totals.subTotal : record.sub_total) });
  totalsRows.push({ label: "Discount", value: `- ${numberFormat(isIndividual ? totals.discountTotal : record.total_discount)}`, credit: true });
  totalsRows.push({ label: taxLabel, value: numberFormat(record.total_tax) });
  if (Number(record.other_service_cost) > 0) {
    totalsRows.push({ label: "Additional Expense", value: numberFormat(record.other_service_cost) });
  }

  const config = {
    docTitle: "SALES ORDER",
    docNumber: record.sale_order_no,
    institution: { logoUrl: institution?.logo, companyName: institution?.name || authInfo?.userInfo?.branch_name || "Institution", companyGstin: null },
    party: {
      billToLabel: "Bill To",
      billTo: {
        name: record.acc_name,
        lines: [
          record.acc_mgr ? `${accMgrLabel}: ${record.acc_mgr}` : null,
          record.address ? `Address: ${record.address}` : null,
          record.contact_no ? `Contact No: ${record.contact_no}` : null,
          record.vat_no ? `${taxRegLabel}: ${record.vat_no}` : null,
        ].filter(Boolean),
      },
      fromLabel: "From",
      from: {
        name: institution?.name || authInfo?.userInfo?.branch_name || "Institution",
        lines: [
          institution?.street_address || null,
          institution?.phone_no ? `+${institution.phone_no}` : null,
        ].filter(Boolean),
      },
      metaLabel: "Order Details",
      meta: [
        { label: "Order Date", value: (formatToCompanyTimezone(record.created_date) || "").split(" ")[0] },
        ...(institution?.vat_license ? [{ label: taxRegLabel, value: institution.vat_license }] : []),
        ...(record.customer_po_ref ? [{ label: "Customer PO Ref", value: record.customer_po_ref }] : []),
        ...(record.payment_terms ? [{ label: "Payment Terms", value: `${record.payment_terms} Days` }] : []),
        ...(record.licence_no ? [{ label: "License No", value: record.licence_no }] : []),
      ],
    },
    columns,
    rows,
    amountFn: (slice) => numberFormat(slice.reduce((sum, r) => sum + (r.rawAmount || 0), 0)),
    totalItems: items.length,
    totalQty: items.reduce((s, d) => s + (Number(d.item_qty) || 0), 0),
    totalsRows,
    grandTotalLabel: `Grand Total (${currency || ""})`,
    grandTotalValue: numberFormat((record.total_amount || 0) + serviceCost),
    paidValue: null,
    dueValue: null,
    amountInWordsText: convertAmountToWordsDesignA(grandTotalInWords, { country, currency }),
    // Added deliberately beyond legacy parity -- see salesOrderAdapter.js's
    // matching note: a Sales Order is often paid against in advance before
    // the invoice exists, so this points the other way from Sales
    // Return/Quotation, where the company is never the one being paid.
    bankDetails: bankAccountToConfig(paymentBankAccount || primaryBankAccount),
    taxSummary: null,
    terms: record.narration ? `Description: ${record.narration}` : "Thank you for your order.",
    signatory: {
      companyName: institution?.name || authInfo?.userInfo?.branch_name || "Institution",
      stampUrl: showStamp ? institution?.stamp : null,
      signatureUrl: showSignature ? institution?.signature : null,
    },
    bonusNote: hasBonusItems ? "*Bonus items are supplied free of cost and not included in taxable value." : null,
    rowBudgets: SALES_ORDER_ROW_BUDGETS,
    tableClassName: "da-table--rich-item",
  };

  return <VoucherBodyDesignA config={config} />;
}
